import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Building2, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function SchoolSetupCard() {
  const { refresh, user } = useAuth();
  const [form, setForm] = useState({ nom: "", ia: "", ief: "", annee_scolaire: "2025-2026" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/schools", form);
      toast.success("École configurée — bienvenue !");
      await refresh();
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setLoading(false);
    }
  };

  const isMaitre = user?.role === "maitre";

  return (
    <div className="ek-card p-6 md:p-8 border-2 border-[#E6B800] bg-[#FCF8E6]/40">
      <div className="flex items-start gap-4">
        <div className="w-12 h-12 rounded-xl bg-[#1F7A63] grid place-items-center shrink-0">
          <Building2 className="w-6 h-6 text-white" />
        </div>
        <div className="flex-1">
          <h2 className="text-xl sm:text-2xl font-bold" style={{ fontFamily: "Outfit" }}>
            Configurez votre école pour commencer
          </h2>
          <p className="text-sm text-[#5C736A] mt-1">
            {isMaitre
              ? "En tant que maître indépendant, renseignez les informations de votre école pour pouvoir créer vos classes et vos élèves."
              : "Renseignez les informations de votre école pour pouvoir créer vos maîtres, vos classes et vos élèves."}
          </p>
        </div>
      </div>
      <form onSubmit={submit} className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="school-setup-form">
        <div className="md:col-span-2">
          <Label>Nom de l&apos;école *</Label>
          <Input
            required
            data-testid="setup-nom"
            value={form.nom}
            onChange={(e) => setForm({ ...form, nom: e.target.value })}
            placeholder="ex : École Primaire de Kédougou"
          />
        </div>
        <div>
          <Label>IA (Inspection Académique)</Label>
          <Input
            data-testid="setup-ia"
            value={form.ia}
            onChange={(e) => setForm({ ...form, ia: e.target.value })}
            placeholder="ex : IA Kédougou"
          />
        </div>
        <div>
          <Label>IEF</Label>
          <Input
            data-testid="setup-ief"
            value={form.ief}
            onChange={(e) => setForm({ ...form, ief: e.target.value })}
            placeholder="ex : IEF Kédougou Commune"
          />
        </div>
        <div className="md:col-span-2">
          <Label>Année scolaire</Label>
          <Input
            data-testid="setup-annee"
            value={form.annee_scolaire}
            onChange={(e) => setForm({ ...form, annee_scolaire: e.target.value })}
            placeholder="2025-2026"
          />
        </div>
        <div className="md:col-span-2 flex justify-end">
          <Button
            type="submit"
            disabled={loading}
            className="bg-[#1F7A63] hover:bg-[#165c4b] text-white h-11 px-6"
            data-testid="setup-submit"
          >
            {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
            Configurer mon école
          </Button>
        </div>
      </form>
    </div>
  );
}
